"use client";

import { memo, useCallback, useState } from "react";
import clsx from "clsx";
import type { CarouselProps } from "./types";
import "keen-slider/keen-slider.min.css";
import { KeenSliderInstance, useKeenSlider } from "keen-slider/react";
import { useSliderEdges } from "./hooks/useCarouselEdges";
import { useHorizontalWheelNavigation } from "./hooks/useHorizontalWheelNavigation";
import { CarouselNavigationButton } from "./CarouselNavigationButton";
import { useSyncSlideClasses } from "./hooks/useSyncSlideClasses";
import { cn } from "@/lib/utils";

const Carousel: React.FC<CarouselProps> = ({
  className,
  navigationClassName,
  children,
  overflowVisible = false,
  showNavigation = false,
  showPagination = false,
  navigationSlot,
  getSlider,
  options = {},
  ...rest
}) => {
  const [sliderReady, setSliderReady] = useState<KeenSliderInstance | null>(
    null
  );
  const [currentSlide, setCurrentSlide] = useState(0);
  const { syncSlideClasses } = useSyncSlideClasses();

  const { isBeginning, isEnd, canScroll, updateEdges } =
    useSliderEdges(sliderReady);

  useHorizontalWheelNavigation({
    slider: sliderReady,
    container: sliderReady?.container,
  });

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    slides: {
      perView: "auto",
    },
    selector: "[data-carousel-slide]",
    created: (slider) => {
      updateEdges(slider);
      setSliderReady(slider);
      getSlider?.(slider);
    },
    slideChanged: (slider) => {
      updateEdges(slider);
      setCurrentSlide(slider.track.details.rel);
    },
    detailsChanged: (slider) => {
      syncSlideClasses(slider);
    },
    ...options,
  });

  const goToSlide = useCallback(
    (idx: number) => {
      instanceRef.current?.moveToIdx(idx);
    },
    [instanceRef]
  );

  const goToPrevSlide = useCallback(() => {
    instanceRef.current?.prev();
  }, [instanceRef]);

  const goToNextSlide = useCallback(() => {
    instanceRef.current?.next();
  }, [instanceRef]);

  return (
    <div className="grid grid-cols-1 place-content-start">
      <div
        ref={sliderRef}
        className={clsx(
          "keen-slider",
          { "overflow-visible": overflowVisible },
          className
        )}
        {...rest}
      >
        {children}
      </div>
      {sliderReady && showPagination && (
        <div className="flex items-center justify-center gap-6 mt-6 py-4">
          {sliderReady?.track?.details?.slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={cn(
                "h-5 w-5 bg-neutral-300 rounded-full cursor-pointer relative outline-none inline-flex select-none shrink-0 transition-colors disabled:cursor-not-allowed",
                {
                  "!bg-blue-500": currentSlide === idx,
                }
              )}
            />
          ))}
        </div>
      )}
      {showNavigation && (
        <div className={cn("mt-6 flex items-center gap-6", navigationClassName)}>
          {canScroll && (
            <>
              <CarouselNavigationButton
                onClick={goToPrevSlide}
                reverse
                disabled={isBeginning}
                aria-disabled={isBeginning}
              />
              <CarouselNavigationButton
                onClick={goToNextSlide}
                disabled={isEnd}
                aria-disabled={isEnd}
              />
            </>
          )}
          {navigationSlot}
        </div>
      )}
    </div>
  );
};

export default memo(Carousel);
